import { FeatureStage } from '../interfaces/usage.interface';

export type StageChipKind = 'adopted' | 'tried' | 'lapsed' | 'never' | 'notonplan';

export interface StageChip {
  label: string;
  cssClass: string;
  kind: StageChipKind;
  /** Sort order for stage columns: Not on plan < Never used < Tried < Lapsed < Adopted */
  rank: number;
}

/** Adoption thresholds as served by GetFeatureAdoptionThresholds, the single source of the rule numbers */
export interface FeatureAdoptionRules {
  DailyLogCoveragePercent: number;
  TakeoffAdoptedEstimates90d: number;
  FormulaSharePercent: number;
  ConnectAdoptedCalls30d: number;
}

export interface AdoptionRuleText {
  dailyLogs: string;
  takeoffs: string;
  formulas: string;
  connect: string;
}

/** Without the served numbers the tooltips stay qualitative rather than risk showing wrong thresholds */
export function adoptionRuleText(rules: FeatureAdoptionRules | null | undefined): AdoptionRuleText {
  const valid = !!rules && [rules.DailyLogCoveragePercent, rules.TakeoffAdoptedEstimates90d, rules.FormulaSharePercent,
    rules.ConnectAdoptedCalls30d].every((n) => typeof n === 'number' && isFinite(n));
  const r = valid ? rules! : null;
  return {
    dailyLogs: 'Daily cadence. Adopted when logs cover '
      + (r ? `${r.DailyLogCoveragePercent}% or more of workdays` : 'most workdays') + ' in the last 30 days.',
    takeoffs: 'Per-estimate cadence. Adopted at '
      + (r ? `${r.TakeoffAdoptedEstimates90d} or more estimates` : 'several estimates')
      + ' with takeoff updates in the last 90 days. Opening the takeoff screen does not count.',
    formulas: 'Per-estimate cadence. Adopted when '
      + (r ? `${r.FormulaSharePercent}% or more of sent estimates` : 'a steady share of sent estimates')
      + ' in the last 90 days use a formula. Formulas come with Takeoffs and Assemblies.',
    connect: 'Measured by calls. Adopted at '
      + (r ? `${r.ConnectAdoptedCalls30d} or more calls` : 'a steady volume of calls')
      + ' in the last 30 days. Lapsed: no calls in 30 days after earlier use.',
  };
}

const CHIPS: { [kind in StageChipKind]: StageChip } = {
  notonplan: { label: 'Not on plan', cssClass: 'usage-stage-chip usage-stage-notonplan', kind: 'notonplan', rank: 0 },
  never: { label: 'Never used', cssClass: 'usage-stage-chip usage-stage-never', kind: 'never', rank: 1 },
  tried: { label: 'Tried', cssClass: 'usage-stage-chip usage-stage-tried', kind: 'tried', rank: 2 },
  lapsed: { label: 'Lapsed', cssClass: 'usage-stage-chip usage-stage-lapsed', kind: 'lapsed', rank: 3 },
  adopted: { label: 'Adopted', cssClass: 'usage-stage-chip usage-stage-adopted', kind: 'adopted', rank: 4 },
};

const KIND_BY_STAGE: { [stage in FeatureStage]: StageChipKind } = {
  NeverUsed: 'never',
  Tried: 'tried',
  Lapsed: 'lapsed',
  Adopted: 'adopted',
};

export const STAGE_KINDS_IN_ORDER: StageChipKind[] = ['adopted', 'tried', 'lapsed', 'never', 'notonplan'];

export function stageChipFor(kind: StageChipKind): StageChip {
  return CHIPS[kind];
}

/** Entitlement comes from the backend gate; null means the feature has no plan row, so it is never "not on plan" */
export function isNotOnPlan(stage: string | null | undefined, isEntitled: boolean | null | undefined): boolean {
  return stage === 'NeverUsed' && isEntitled === false;
}

/** Null when the stage is unknown, so callers can render a placeholder instead of a guess */
export function stageChip(stage: string | null | undefined, isEntitled: boolean | null | undefined): StageChip | null {
  if (isNotOnPlan(stage, isEntitled)) {
    return CHIPS.notonplan;
  }
  return stageOnlyChip(stage);
}

/** The stage alone, without the plan overlay; used where a past stage is shown */
export function stageOnlyChip(stage: string | null | undefined): StageChip | null {
  const kind = stage ? KIND_BY_STAGE[stage as FeatureStage] : undefined;
  return kind ? CHIPS[kind] : null;
}

/** "Top N% of companies using it"; N never rounds down to 0 */
export function peerLine(peerPercentile: number | null | undefined): string | null {
  if (peerPercentile === null || peerPercentile === undefined) {
    return null;
  }
  return 'Top ' + Math.max(1, Math.round(100 - peerPercentile)) + '% of companies using it';
}

export function percentOf(part: number, whole: number): number | null {
  return whole > 0 ? Math.round((100 * part) / whole) : null;
}
